import { StatePage } from "@/components/state-page";
import { filterKeys, withDefaults } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { CONSTANTS } from "@/utils/constants";
import { getPrivateKey } from "@/utils/crypto";
import decryptNId from "@/utils/decrypt-nid";
import { requirePermissions } from "@/utils/permissions";
import type { APIOutput } from "@/utils/types";
import { Button } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AdminClient } from "@workspace/admin-domain/src/features/client/schema";
import { t } from "i18next";
import { LucideDownload, LucidePlus } from "lucide-react";
import ControlBar from "./-control-bar";
import NewClient from "./-new";
import ClientTable from "./-table";
import { ClientFiltersSchema, DEFAULT_CLIENTS_PARAMS } from "./-utils";

export const Route = createFileRoute("/_authenticated/clients/")({
  validateSearch: ClientFiltersSchema.extend(withDefaults(ClientFiltersSchema, DEFAULT_CLIENTS_PARAMS).shape),
  component: Component,
  beforeLoad: requirePermissions("client:get"),
  loaderDeps: ({ search }) => [search] as const,
  loader: ({ context, deps: [search] }) => {
    void context.queryClient.prefetchQuery(
      orpc.client.getAll.queryOptions({
        input: { ...DEFAULT_CLIENTS_PARAMS, ...search },
      }),
    );
    void context.queryClient.prefetchQuery(
      orpc.client.count.queryOptions({
        input: search,
      }),
    );
  },
  errorComponent: ({ error, reset }) => (
    <StatePage error={error} reset={reset} resourceType="clients" emptyResourceType="clients" />
  ),
  notFoundComponent: () => <StatePage isNotFound resourceType="clients" emptyResourceType="clients" />,
});

function Component() {
  const search = Route.useSearch();
  const queryClient = useQueryClient();

  const filters = filterKeys(search, AdminClient.GetAll.shape);

  const exportAllMutation = useMutation({
    mutationFn: async () => {
      const [{ pdf, Document }, { ClientPDFPage }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("./-pdf-document"),
      ]);

      const agency = await queryClient.fetchQuery(orpc.agency.get.queryOptions());

      // Fetch all clients with pagination
      const allClients: APIOutput["client"]["getAll"] = [];
      let page = 0;
      const limit = 1000;
      let hasMore = true;

      while (hasMore) {
        const clients = await queryClient.fetchQuery(
          orpc.client.getAll.queryOptions({
            input: {
              ...DEFAULT_CLIENTS_PARAMS,
              ...filters,
              page,
              limit,
              sortBy: filters.sortBy ?? DEFAULT_CLIENTS_PARAMS.sortBy,
              sortOrder: filters.sortOrder ?? DEFAULT_CLIENTS_PARAMS.sortOrder,
            },
          }),
        );

        allClients.push(...clients);

        if (clients.length < limit) {
          hasMore = false;
        } else {
          page++;
        }
      }

      if (allClients.length === 0) {
        throw new Error(t("clients.exportPDF.all.empty"));
      }

      const isLocked = (await getPrivateKey()) === null;

      // Fetch full details for each client and prepare PDF pages
      const clientPages = await Promise.all(
        allClients.map(async (clientSummary) => {
          const [clientData, reservations] = await Promise.all([
            queryClient.fetchQuery(
              orpc.client.get.queryOptions({
                input: { id: clientSummary.id },
              }),
            ),
            queryClient.fetchQuery(
              orpc.reservation.getAll.queryOptions({
                input: {
                  value: clientSummary.id,
                  type: "agencyClientId",
                },
              }),
            ),
          ]);

          let nationalIdImages: { front: string; back: string } | null = null;

          if (
            !isLocked &&
            clientData.encryptionKey &&
            clientData.nIdFrontKey &&
            clientData.nIdFrontIv &&
            clientData.nIdBackKey &&
            clientData.nIdBackIv
          ) {
            try {
              nationalIdImages = await decryptNId({
                nIdFrontIv: clientData.nIdFrontIv,
                nIdBackIv: clientData.nIdBackIv,
                nIdFrontKey: clientData.nIdFrontKey,
                nIdBackKey: clientData.nIdBackKey,
                encryptionKey: clientData.encryptionKey,
              });
            } catch (error) {
              console.warn("Failed to decrypt NID for client", error);
            }
          }

          return (
            <ClientPDFPage
              key={clientData.id}
              client={clientData}
              agency={agency}
              reservations={reservations}
              nId={nationalIdImages}
              baseUrl={window.location.origin}
            />
          );
        }),
      );

      const doc = <Document>{clientPages}</Document>;

      const blob = await pdf(doc).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const dateStr = new Date().toISOString().split("T")[0];
      link.download = `clients-${dateStr}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      notifications.show({
        title: t("success"),
        message: t("clients.exportPDF.all.success.description"),
        color: "green",
      });
    },
    onError: (error) => {
      notifications.show({
        title: t("error.title"),
        message: error instanceof Error ? error.message : t("clients.exportPDF.all.error.description"),
        color: "red",
      });
    },
  });

  return (
    <div className="gap-md md:gap-lg flex grow flex-col">
      <div className="py-md flex flex-wrap items-center justify-between gap-x-6 gap-y-4 md:justify-start">
        <p className="text-2xl font-semibold md:text-3xl">{t("clients.yourClients")}</p>
        <div className="grid grow grid-cols-2 items-center gap-1.5 md:flex">
          <Button
            variant="default"
            leftSection={<LucideDownload className="size-4" />}
            onClick={() => exportAllMutation.mutate()}
            disabled={CONSTANTS.DEMO}
            loading={exportAllMutation.isPending}
          >
            {t("export")}
          </Button>
          <NewClient
            trigger={(onClick) => (
              <Button onClick={onClick} leftSection={<LucidePlus />}>
                {t("clients.new")}
              </Button>
            )}
            onSuccess={() => {
              void queryClient.invalidateQueries({
                queryKey: orpc.client.getAll.key({
                  input: { ...DEFAULT_CLIENTS_PARAMS, ...filters },
                }),
              });
            }}
          />
        </div>
      </div>
      <ControlBar />
      <ClientTable />
    </div>
  );
}
